import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import HandleChange from "../../../../../Helpers/HandleChange";
import { TAB_OPTIONS, TrafficFinesTabs } from "../Model/Model";
import { SEARCH_MODEL } from "../../../../../components/table/Model/TableModel";
import { DEFAULT_LATITUDE, DEFAULT_LONGITUDE, tryGetAddress } from "../../../../../Apis/functions";
import { TABLES } from "../../../../../Apis/Tables";
import { permissionObj } from "../../../../../Helpers/permission";
import { debounce } from "lodash";

/**
 * @Author       Karen Yunuen Pozos Arias
 * @JobTitle     FullStack Developer
 * @Email        kpozos@ibl.mx
 *
 * @Description
 * Custom React hook that encapsulates the main logic for the **Traffic Fines Dashboard**.
 *
 * It manages:
 * - Modal state and active tabs
 * - Map location and reverse geocoding
 * - Permissions for viewing fines and geofences
 * - Search behavior and debounced map updates
 * - Integration with Redux state and i18n translation
 *
 * @returns {Object} Hook state, utility handlers, and configuration data.
 *
 * @example
 * const {
 *   t,
 *   totalElements,
 *   setTotalElements,
 *   permission,
 *   direction,
 *   handleMapMove,
 *   TrafficFinesTabsPermission
 * } = DashboardApp();
 */
const DashboardApp = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    /** -------------------------------
     *  Local state management
     *  ------------------------------- */
    const [item, setItem] = useState({});
    const [searchLocation, setSearchLocation] = useState("");
    const [direction, setDirection] = useState("");
    const [selectEmployee, setSelectEmployee] = useState(null);
    const [reset, setReset] = useState(false);
    const [totalElements, setTotalElements] = useState(0);
    const elementRef = useRef(null);

    /** Table option and search configuration */
    const option = TABLES.FINE_USER;
    const bodyAutocompleteSearch = SEARCH_MODEL.setObject({ page: 0, size: 20, model: option });

    /** Model handler utility */
    const { handleChange } = HandleChange({ model: item, setModel: setItem });

    /** Redux state */
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);

    /**
     * Initializes the default map coordinates on first render.
     *
     * @effect
     * @dependencies []
     */
    useEffect(() => {
        setItem(prevState => ({
            ...prevState,
            latitude: DEFAULT_LATITUDE,
            longitude: DEFAULT_LONGITUDE,
        }));
    }, []);

    /**
     * Updates a CSS variable with the element’s top offset whenever `item` changes.
     * Used for positioning map-related elements dynamically.
     *
     * @effect
     * @dependencies [item]
     */
    useLayoutEffect(() => {
        if (elementRef?.current) {
            const rect = elementRef.current.getBoundingClientRect();
            elementRef.current.style.setProperty("--top-value", `${rect.top}px`);
        }
    }, [item]);

    /**
     * Filters available dashboard tabs based on user permissions.
     *
     * @constant
     * @type {Array<Object>}
     */
    const TrafficFinesTabsPermission = TrafficFinesTabs.filter(item => {
        if (item.key === TAB_OPTIONS.GEOFENCES) {
            return permission?.includes(permissionObj.WEB_TRAFFIC_DASHBOARD_GEOFENCES_READ);
        }
        if (item.key === TAB_OPTIONS.FINES) {
            return permission?.includes(permissionObj.WEB_TRAFFIC_DASHBOARD_FINES_READ);
        }
        return false;
    });

    /**
     * Handles map movement events by fetching the corresponding address.
     * The function is debounced to avoid excessive API calls.
     *
     * @function
     * @param {number} lat - Latitude of the new map center
     * @param {number} lng - Longitude of the new map center
     *
     * @example
     * handleMapMove(19.4326, -99.1332);
     */
    const handleMapMove = debounce((lat, lng) => {
        tryGetAddress(lat, lng, dispatch, setDirection);
    }, 800);

    return {
        t,
        totalElements,
        setTotalElements,
        DEV,
        permission,
        searchLocation,
        handleChange,
        selectEmployee,
        setSelectEmployee,
        direction,
        setDirection,
        handleMapMove,
        dispatch,
        bodyAutocompleteSearch,
        TrafficFinesTabsPermission,
        option,
        reset,
        setReset
    };
};

export default DashboardApp;
